import { Calendar } from 'lucide-react';
import MonthMultiSelect from './MonthMultiSelect';

interface FiscalYearSelectorProps {
  value: number;
  period: 'year' | 'quarter' | 'month';
  selectedPeriod: string;
  onChange: (year: number, period: 'year' | 'quarter' | 'month', selectedPeriod: string) => void;
  className?: string;
  variant?: 'light' | 'dark';
}

const SELECT_CLS: Record<'light' | 'dark', string> = {
  light: 'px-3 py-2 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors cursor-pointer',
  dark:  'px-3 py-2 text-sm border border-white/15 rounded-xl text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition-colors cursor-pointer [&>option]:bg-slate-800 [&>option]:text-white',
};

export default function FiscalYearSelector({ value, period, selectedPeriod, onChange, className = '', variant = 'light' }: FiscalYearSelectorProps) {
  const selectCls = SELECT_CLS[variant];
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const currentFiscalYear = currentMonth < 4 ? currentYear - 1 : currentYear;

  const years = Array.from({ length: 11 }, (_, i) => currentFiscalYear - 5 + i);

  const quarters = [
    { value: '1', label: '1er trim. (Avr – Juin)' },
    { value: '2', label: '2ème trim. (Juil – Sep)' },
    { value: '3', label: '3ème trim. (Oct – Déc)' },
    { value: '4', label: '4ème trim. (Jan – Mar)' }
  ];

  const handlePeriodTypeChange = (newPeriod: 'year' | 'quarter' | 'month') => {
    const defaultMonth = currentMonth >= 4 ? String(currentMonth).padStart(2, '0') : '04';
    onChange(value, newPeriod, newPeriod === 'month' ? defaultMonth : '1');
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <Calendar size={15} className={variant === 'dark' ? 'text-white/60 shrink-0' : 'text-slate-400 shrink-0'} />
      <select
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value), period, selectedPeriod)}
        className={selectCls}
      >
        {years.map(year => (
          <option key={year} value={year}>Année {year} – {year + 1}</option>
        ))}
      </select>

      <select
        value={period}
        onChange={(e) => handlePeriodTypeChange(e.target.value as 'year' | 'quarter' | 'month')}
        className={selectCls}
      >
        <option value="year">Année complète</option>
        <option value="quarter">Trimestre</option>
        <option value="month">Mois</option>
      </select>

      {period === 'quarter' && (
        <select
          value={selectedPeriod}
          onChange={(e) => onChange(value, period, e.target.value)}
          className={selectCls}
        >
          {quarters.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
        </select>
      )}

      {period === 'month' && (
        <MonthMultiSelect
          selectedPeriod={selectedPeriod}
          onChange={(months) => onChange(value, period, months)}
          className={selectCls}
        />
      )}
    </div>
  );
}
